// ════════════════════════════════════════════════════════════
//  ui/BeingCard.jsx — THE FIGHTER'S CARD (caP'n 2026-10-22: "a baseball card style profile card")
// ════════════════════════════════════════════════════════════
//  Front: who they are — their three, their name, their number, where they stand. Tap it: the back, every stat
//  worth a fan's time (online/cards.js beingCard — pure, from the record). Works for NPCs and Challengers.
//  Styles in league3.css (.fc-*). The flip is transform-only and stops under prefers-reduced-motion.
// ════════════════════════════════════════════════════════════
import { useState } from "react";
import { LINES, portraitUrl } from "../data/roster.js";
import { cardTag } from "../online/cards.js";
import { crownTier } from "../online/circuit.js";
import { Crown, RankPill } from "./Crown.jsx";

const mmss = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);
const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const streakTxt = (n) => (n > 0 ? `W${n}` : n < 0 ? `L${-n}` : "—");

/** card = beingCard(...) · team = their three now · nameOf(key) for opponents · onOpen(key) opens another card · fav: followed */
export default function BeingCard({ card: c, team, nameOf, onOpen, fav = false }) {
  const [back, setBack] = useState(false);
  const flip = () => setBack((b) => !b);
  const div = c.challenger ? "ch" : c.division === "circuit" ? "c" : "k";
  const who = (k) => (k ? <b className="being-link" onClick={(e) => { e.stopPropagation(); onOpen && onOpen(k); }}>{nameOf(k)}</b> : "—");
  const Stat = ({ label, value, sub }) => <div className="fc-stat"><em>{label}</em><b>{value}</b>{sub && <small>{sub}</small>}</div>;
  return (
    <div className={"fc " + "fc-" + div + (back ? " flipped" : "")} role="button" tabIndex={0} aria-label={`${c.name}'s card — tap to flip`}
      onClick={flip} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); flip(); } }}>
      <div className="fc-in">
        <div className="fc-face fc-front">
          <div className="fc-top">
            <span className="fc-num">{c.challenger ? "CH" : `#${c.number}`}</span>
            <span className="fc-div">{c.division === "circuit" ? "The Circuit" : c.division === "contenders" ? "The Contenders" : "—"}{c.place && (c.division === "circuit" || c.division === "contenders") ? <> · <RankPill div={c.division} pos={c.place} title="Place on this week's table" /></> : c.place ? ` · #${c.place}` : ""}</span>
            {fav && <span className="fc-fav" title="You follow them">★</span>}
          </div>
          <div className="fc-art">
            {(team || []).map((id, i) => LINES[id] ? <i key={i} style={{ backgroundImage: `url(${portraitUrl(id, "lord")})` }} title={LINES[id].lord} /> : null)}
          </div>
          <div className="fc-name">{c.name}</div>
          <div className="fc-tag">{cardTag(c)}{c.escaped ? " · escaped this week" : c.fell ? " · fell this week" : ""}</div>
          <div className="fc-lords">{(team || []).map((id) => (LINES[id] ? LINES[id].lord : id)).join(" · ")}</div>
          <div className="fc-foot">
            <span><b>{c.season.w}–{c.season.l}</b> season</span>
            <span><b>{c.rating}</b> rating</span>
            <span><b>{streakTxt(c.streak)}</b> now</span>
          </div>
          <div className="fc-hint">tap for the stats</div>
        </div>
        <div className="fc-face fc-back">
          <div className="fc-back-h">{c.name}<small>{c.challenger ? "Challenger" : `#${c.number}`}</small></div>
          <div className="fc-grid">
            <Stat label="Season" value={`${c.season.w}–${c.season.l}`} sub={pct(c.season.pct)} />
            <Stat label="This week" value={`${c.week.w}–${c.week.l}`} sub={c.place ? `#${c.place}` : null} />
            <Stat label="Rating" value={c.rating} sub={c.peak ? `peak ${c.peak}` : null} />
            <Stat label="Streak" value={streakTxt(c.streak)} sub={c.longest ? `best W${c.longest}` : null} />
            <Stat label="Fastest win" value={mmss(c.fastest)} sub={c.avgWin ? `avg ${mmss(c.avgWin)}` : null} />
            <Stat label="Ruins" value={c.ruins} sub={c.closeWins ? `${c.closeWins} won at the bell` : null} />
            <Stat label="Upsets" value={c.upsets} />
            <Stat label="Anima / win" value={c.animaPerWin ?? "—"} />
            {c.points && <Stat label="Points" value={c.points.score.toLocaleString()} sub={`C ${c.points.cp.toLocaleString()} · K ${c.points.kp.toLocaleString()}`} />}
            {(c.apo.w + c.apo.l > 0) && <Stat label="Apocalypse" value={`${c.apo.w}–${c.apo.l}`} />}
          </div>
          {c.cups.length > 0 && <div className="fc-cups">{c.cups.map((x) => <span key={x.c}><Crown tier={crownTier(x.c)} size={17} title={`${x.name} crown`} />{x.name}{x.n > 1 ? ` ×${x.n}` : ""}</span>)}</div>}
          <div className="fc-lines">
            {c.best && <div><em>Biggest win</em>{who(c.best.opp)}</div>}
            {c.victim && <div><em>Owns</em>{who(c.victim.key)}<small>{c.victim.w}–{c.victim.l}</small></div>}
            {c.nemesis && <div><em>Nemesis</em>{who(c.nemesis.key)}<small>{c.nemesis.w}–{c.nemesis.l}</small></div>}
          </div>
          <div className="fc-hint">tap to flip back</div>
        </div>
      </div>
    </div>
  );
}
